import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Shield, ShieldOff, Mail, UserCog, Copy, Check, KeyRound, AlertTriangle, Lock } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Chip } from '@/components/ui/Chip';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useUsers, useCreateUser, useSetUserActive,
  useRequestPasswordResetLink, useSetUserPassword,
} from '@/hooks/useUsers';
import { useRoles, useSetUserRoleId } from '@/hooks/useRoles';
import { RoleMatrixEditor } from '@/components/settings/RoleMatrixEditor';
import { cn } from '@/lib/format';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/format';
import type { UserRole } from '@/types/domain';

interface FormValues {
  email: string;
  displayName: string;
  role: UserRole;
  password: string;
}

export default function Users() {
  const { user: me } = useAuth();
  const { data: users, isLoading } = useUsers();
  const createUser = useCreateUser();
  const setRoleId = useSetUserRoleId();
  const setActive = useSetUserActive();

  const { data: roles } = useRoles();
  const [tab, setTab] = useState<'users' | 'roles'>('users');
  const [createOpen, setCreateOpen] = useState(false);
  const [createResult, setCreateResult] = useState<{ email: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [resetTarget, setResetTarget] = useState<{ email: string; name: string } | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const resetMut = useRequestPasswordResetLink();

  const issueResetLink = async () => {
    if (!resetTarget) return;
    setResetError(null);
    setResetLink(null);
    setLinkCopied(false);
    try {
      setResetLink(await resetMut.mutateAsync(resetTarget.email));
    } catch (e) {
      setResetError(e instanceof Error ? e.message : 'Gagal membuat link');
    }
  };

  // Direct password set — the owner types it while the person is standing
  // there. Kept separate from the link flow so neither dialog grows modes.
  const [pwTarget, setPwTarget] = useState<{ uid: string; email: string; name: string } | null>(null);
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwDone, setPwDone] = useState(false);
  const setPwMut = useSetUserPassword();

  const closePw = () => {
    setPwTarget(null);
    setNewPw('');
    setNewPw2('');
    setPwError(null);
    setPwDone(false);
  };

  const submitPw = async () => {
    if (!pwTarget) return;
    setPwError(null);
    if (newPw.length < 8) return setPwError('Password minimal 8 karakter.');
    if (newPw !== newPw2) return setPwError('Konfirmasi password tidak sama.');
    try {
      await setPwMut.mutateAsync({ uid: pwTarget.uid, password: newPw });
      setPwDone(true);
    } catch (e) {
      setPwError(e instanceof Error ? e.message : 'Gagal mengganti password');
    }
  };

  // Email route: GoTrue sends the recovery mail itself (SMTP via Resend), using
  // the template in public/mail/recovery.html — which also links via token_hash.
  const [emailState, setEmailState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const sendResetEmail = async () => {
    if (!resetTarget) return;
    setResetError(null);
    setEmailState('sending');
    const { error } = await supabase.auth.resetPasswordForEmail(resetTarget.email);
    if (error) {
      setEmailState('idle');
      setResetError(error.message);
      return;
    }
    setEmailState('sent');
  };

  const closeReset = () => {
    setEmailState('idle');
    // The link is a live credential; drop it from memory with the dialog rather
    // than leaving it in state for the rest of the session.
    setResetTarget(null);
    setResetLink(null);
    setResetError(null);
    setLinkCopied(false);
  };

  const [confirmDisable, setConfirmDisable] = useState<{ uid: string; disable: boolean; name: string } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { email: '', displayName: '', role: 'cashier', password: '' },
  });

  const onSubmitCreate = async (values: FormValues) => {
    await createUser.mutateAsync({
      email: values.email.trim(),
      displayName: values.displayName.trim(),
      role: values.role,
      password: values.password,
    });
    setCreateResult({ email: values.email.trim(), password: values.password });
    reset();
  };

  const handleDisable = async () => {
    if (!confirmDisable) return;
    await setActive.mutateAsync({ uid: confirmDisable.uid, disabled: confirmDisable.disable });
    setConfirmDisable(null);
  };

  const copyPassword = () => {
    if (!createResult) return;
    navigator.clipboard.writeText(
      `Email: ${createResult.email}\nPassword: ${createResult.password}`,
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateResult(null);
    reset();
  };

  return (
    <>
      <PageHeader
        overline="Manajemen akses"
        title="Users"
        description="Kelola siapa yang punya akun, dan apa yang boleh dilakukan setiap role."
        actions={
          tab === 'users' ? (
            <Button
              variant="primary"
              leftIcon={<Plus size={16} />}
              onClick={() => setCreateOpen(true)}
            >
              Invite user
            </Button>
          ) : undefined
        }
      />

      {/* Two halves of one question — who exists, and what they may do. Keeping
          them on one page means assigning a role and reading what that role
          actually grants are one click apart, not two screens apart. */}
      <div className="flex items-center gap-1 border-b border-line mb-5">
        {([
          { id: 'users', label: `User (${users?.length ?? 0})` },
          { id: 'roles', label: `Role & Hak Akses (${roles?.length ?? 0})` },
        ] as const).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'px-4 py-2.5 text-small font-medium border-b-2 -mb-px transition-colors',
              tab === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-ink-subtle hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'roles' && <RoleMatrixEditor />}

      <Card data-tour="users-table" padding="none" className={tab === 'users' ? undefined : 'hidden'}>
        {isLoading ? (
          <div className="py-16 text-center text-ink-subtle text-body">Loading users…</div>
        ) : !users || users.length === 0 ? (
          <EmptyState
            icon={<UserCog size={24} strokeWidth={1.5} />}
            title="Belum ada user lain"
            description="Invite cashier untuk mulai pakai POS bareng kamu."
            action={
              <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
                Invite user
              </Button>
            }
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>User</TH>
                <TH>Email</TH>
                <TH>Role</TH>
                <TH>Status</TH>
                <TH>Dibuat</TH>
                <TH className="text-right">Aksi</TH>
              </TR>
            </THead>
            <TBody>
              {users.map((u) => {
                const isMe = u.uid === me?.uid;
                return (
                  <TR key={u.uid}>
                    <TD>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-small font-medium">
                          {u.displayName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-ink">{u.displayName}</p>
                          {isMe && (
                            <p className="text-caption text-primary">Kamu</p>
                          )}
                        </div>
                      </div>
                    </TD>
                    <TD>
                      <span className="text-small text-ink-subtle">{u.email}</span>
                    </TD>
                    <TD>
                      {/* The full role list, not the old two-value enum. Still
                          disabled on your own row: demoting yourself out of
                          user management is the one change nobody can undo
                          from this screen. */}
                      <select
                        value={u.roleId}
                        onChange={(e) => setRoleId.mutate({ uid: u.uid, roleId: e.target.value })}
                        disabled={isMe || setRoleId.isPending}
                        className="text-small bg-transparent border border-line rounded px-2 py-1 cursor-pointer disabled:opacity-50"
                      >
                        {(roles ?? []).map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    </TD>
                    <TD>
                      <Chip variant={u.isActive ? 'success' : 'default'}>
                        {u.isActive ? 'Active' : 'Disabled'}
                      </Chip>
                    </TD>
                    <TD className="text-small text-ink-subtle whitespace-nowrap">
                      {formatDate(u.createdAt)}
                    </TD>
                    <TD>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPwTarget({ uid: u.uid, email: u.email, name: u.displayName })}
                          title="Ganti password langsung"
                          aria-label={`Ganti password ${u.displayName}`}
                        >
                          <Lock size={16} className="text-ink-subtle" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setResetTarget({ email: u.email, name: u.displayName })}
                          title="Buat link reset password"
                          aria-label={`Buat link reset password untuk ${u.displayName}`}
                        >
                          <KeyRound size={16} className="text-ink-subtle" />
                        </Button>
                        {!isMe && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setConfirmDisable({
                                uid: u.uid,
                                disable: u.isActive,
                                name: u.displayName,
                              })
                            }
                            title={u.isActive ? 'Disable' : 'Enable'}
                            aria-label={u.isActive ? 'Disable' : 'Enable'}
                          >
                            {u.isActive ? (
                              <ShieldOff size={16} className="text-danger" />
                            ) : (
                              <Shield size={16} className="text-success" />
                            )}
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </Card>

      {/* Create user modal */}
      <Modal
        open={createOpen && !createResult}
        onClose={closeCreate}
        title="Invite user"
        description="User baru akan dibuat dengan email dan temporary password."
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmitCreate)} className="space-y-4">
          <Input
            label="Display name"
            placeholder="Nama kasir"
            error={errors.displayName?.message}
            {...register('displayName', { required: 'Nama wajib diisi' })}
          />
          <Input
            label="Email"
            type="email"
            placeholder="cashier@event.id"
            error={errors.email?.message}
            {...register('email', {
              required: 'Email wajib',
              pattern: { value: /^[^\s@]+@[^\s@]+$/, message: 'Format email tidak valid' },
            })}
          />
          <div>
            <label className="block text-small font-medium text-ink mb-1.5">Role</label>
            <select
              {...register('role', { required: true })}
              className="input cursor-pointer"
            >
              <option value="cashier">Cashier — POS, inventory read, returns</option>
              <option value="owner">Owner — full access</option>
            </select>
          </div>
          <Input
            label="Temporary password"
            type="text"
            placeholder="Minimal 8 karakter"
            hint="User harus ganti password setelah login pertama."
            error={errors.password?.message}
            {...register('password', {
              required: 'Password wajib',
              minLength: { value: 8, message: 'Minimal 8 karakter' },
            })}
          />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="ghost" onClick={closeCreate}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={createUser.isPending}>
              Create user
            </Button>
          </div>
        </form>
      </Modal>

      {/* Success modal — show credentials once */}
      <Modal
        open={!!createResult}
        onClose={closeCreate}
        title="User created!"
        description="Simpan password ini — ini hanya ditampilkan sekali."
        size="md"
        footer={
          <Button variant="primary" onClick={closeCreate}>
            Selesai
          </Button>
        }
      >
        {createResult && (
          <div className="space-y-3">
            <div className="p-4 rounded bg-canvas border border-line">
              <p className="text-overline uppercase text-ink-subtle">Email</p>
              <p className="text-body font-mono text-ink mt-1">{createResult.email}</p>
              <p className="text-overline uppercase text-ink-subtle mt-3">Password</p>
              <p className="text-body font-mono text-ink mt-1">{createResult.password}</p>
            </div>
            <Button
              variant="secondary"
              fullWidth
              leftIcon={copied ? <Check size={16} /> : <Copy size={16} />}
              onClick={copyPassword}
            >
              {copied ? 'Copied!' : 'Copy credentials'}
            </Button>
            <p className="text-caption text-ink-subtle text-center">
              <Mail size={12} className="inline mr-1" />
              Kirim credentials ini ke user via channel yang aman (jangan WA publik).
            </p>
          </div>
        )}
      </Modal>

      <Modal
        open={!!pwTarget}
        onClose={closePw}
        title="Ganti password"
        description={pwTarget ? `${pwTarget.name} (${pwTarget.email})` : undefined}
        size="md"
        footer={
          pwDone ? (
            <Button variant="primary" onClick={closePw}>Selesai</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={closePw}>Batal</Button>
              <Button variant="primary" onClick={submitPw} loading={setPwMut.isPending}>
                Simpan password
              </Button>
            </>
          )
        }
      >
        {pwDone ? (
          <div className="text-center space-y-2 py-2">
            <Check size={28} className="mx-auto text-success" />
            <p className="text-small text-ink">
              Password <b>{pwTarget?.name}</b> sudah diganti. Sesi lamanya masih jalan
              sampai token-nya habis (maks. 1 jam); login berikutnya pakai password baru.
            </p>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void submitPw();
            }}
          >
            <Input
              label="Password baru"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              hint="Minimal 8 karakter."
            />
            <Input
              label="Ulangi password baru"
              type="password"
              autoComplete="new-password"
              value={newPw2}
              onChange={(e) => setNewPw2(e.target.value)}
            />
            {pwError && (
              <p role="alert" className="text-caption text-danger">{pwError}</p>
            )}
            <p className="text-caption text-ink-subtle">
              Sampaikan password ini langsung ke orangnya. Password tidak dicatat di
              audit log — hanya bahwa kamu menggantinya. Semua sesi akun itu langsung
              diputus{pwTarget?.uid === me?.uid ? ' — termasuk sesi kamu ini, jadi kamu akan diminta login ulang' : ''}.
            </p>
          </form>
        )}
      </Modal>

      <Modal
        open={!!resetTarget}
        onClose={closeReset}
        title="Reset password"
        description={
          resetTarget
            ? `Buat link untuk ${resetTarget.name} (${resetTarget.email}).`
            : undefined
        }
        size="md"
        footer={
          resetLink || emailState === 'sent' ? (
            <Button variant="primary" onClick={closeReset}>Selesai</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={closeReset}>Batal</Button>
              <Button
                variant="secondary"
                onClick={sendResetEmail}
                loading={emailState === 'sending'}
                leftIcon={<Mail size={16} />}
              >
                Kirim via email
              </Button>
              <Button variant="primary" onClick={issueResetLink} loading={resetMut.isPending}>
                Buat link
              </Button>
            </>
          )
        }
      >
        {emailState === 'sent' ? (
          <div className="text-center space-y-2 py-2">
            <Check size={28} className="mx-auto text-success" />
            <p className="text-small text-ink">
              Email reset dikirim ke <b>{resetTarget?.email}</b>. Minta orangnya cek inbox
              (dan folder spam), lalu tekan <b>Lanjutkan</b> di halaman yang terbuka.
            </p>
          </div>
        ) : !resetLink ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 rounded border border-line bg-canvas text-small text-ink-subtle">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-warning" />
              <span>
                <b>Kirim via email</b> mengirim link ke alamat akun itu.
                <b> Buat link</b> menampilkannya di sini untuk kamu kirim sendiri lewat
                WhatsApp atau chat. Dua-duanya sekali pakai, dan penerima harus menekan
                <b> Lanjutkan</b> di halaman itu dulu — jadi preview link tidak
                menghanguskannya.
              </span>
            </div>
            <p className="text-caption text-ink-subtle">
              Password lama tetap berlaku sampai orangnya membuka link dan menyimpan
              password baru.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-small text-ink">
              Kirim link ini ke <b>{resetTarget?.name}</b>. Berlaku sekali pakai dan
              akan kedaluwarsa.
            </p>
            <div className="p-3 rounded border border-line bg-canvas">
              <code className="block text-caption font-mono break-all text-ink">{resetLink}</code>
            </div>
            <Button
              variant="secondary"
              fullWidth
              leftIcon={linkCopied ? <Check size={16} /> : <Copy size={16} />}
              onClick={async () => {
                await navigator.clipboard.writeText(resetLink);
                setLinkCopied(true);
              }}
            >
              {linkCopied ? 'Link disalin' : 'Salin link'}
            </Button>
            <p className="text-caption text-ink-subtle">
              Perlakukan seperti password: siapa pun yang punya link ini bisa mengatur
              password akun tersebut sampai link kedaluwarsa. Jangan kirim ke grup.
            </p>
          </div>
        )}

        {resetError && (
          <p role="alert" className="mt-3 text-caption text-danger">{resetError}</p>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmDisable}
        onClose={() => setConfirmDisable(null)}
        onConfirm={handleDisable}
        title={confirmDisable?.disable ? 'Disable user?' : 'Enable user?'}
        description={
          confirmDisable?.disable
            ? `${confirmDisable.name} tidak akan bisa login sampai di-enable lagi.`
            : `${confirmDisable?.name} akan bisa login lagi.`
        }
        confirmText={confirmDisable?.disable ? 'Disable' : 'Enable'}
        variant={confirmDisable?.disable ? 'danger' : 'primary'}
        loading={setActive.isPending}
      />
    </>
  );
}
